import {
  type Collection,
  type CountableApiResponse,
  type CreateFileDto,
  CreateFileFormField,
  type CreateFileQuery,
  type CreateFilesDto,
  type FileType,
  type FileValue,
  type SerializedFile,
  type UpdateFileDto,
} from '@enmon/commons/lib/enmon';
import { BaseApiClient } from '@/services/baseApiClient';
import request, { type RequestOptions, uploadFile } from '@/utils/request';
import { getQuery } from '@/components/filter/helpers';
import type { Pagination } from '@/hooks';

export interface FilesFilter extends Pagination {
  invoices?: string[];
  manualReadings?: string[];
  emissionRecords?: string[];
  customEmissionFactorValidities?: string[];
  customEmissionFactors?: string[];
  meters?: string[];
  supplyPoints?: string[];
  customers?: string[];
  establishments?: string[];
  floorPlans?: string[];
  collections?: Collection[];
  revisions?: string[];
  fileTypes?: FileType[];
  fileValues?: FileValue[];
  hasPreview?: boolean;
  extensions?: string[];
}

// Argument types
export type GetFileArg = SerializedFile['_id'];
export type GetFilesArg = FilesFilter;
export type CreateFilesArg = {
  query: CreateFileQuery;
  dto?: CreateFileDto;
  files: File[];
};
export type CreateManyFilesArg = {
  query: CreateFileQuery;
  /** One dto per file, aligned by index with `files`. Use `null` for a file without metadata. */
  dtos: CreateFilesDto;
  files: File[];
};
export type UpdateFileArg = {
  id: SerializedFile['_id'];
  payload: UpdateFileDto;
};
export type DeleteFileArg = SerializedFile['_id'];
export type GetFileLinkArg = SerializedFile['_id'];
/**
 * @deprecated Use `GetFileContentArgV2` instead.
 */
export type GetFileContentArg = Exclude<SerializedFile['bucketPath'], undefined>;
export type GetFileContentArgV2 = SerializedFile['_id'];
export type GetThumbnailArg = SerializedFile['_id'];

// Result types
export type GetFileResult = SerializedFile | null;
export type GetFilesResult = CountableApiResponse<SerializedFile[]> | null;
export type CreateFilesResult = SerializedFile[] | null;
export type UpdateFileResult = SerializedFile | null;
export type DeleteFileResult = undefined | null;
export type GetFileLinkResult = { link: string } | null;
export type GetFileContentResult = Blob | null;
export type GetFileThumbnailResult = { link: string } | null;

export interface IFilesApiClient {
  /**
   * Return single file from files collection where 'file._id' === 'id'
   */
  getOne(id: GetFileArg, signal: RequestOptions['signal']): Promise<GetFileResult>;
  /**
   * Returns list of files from files collection where 'file.meta' satisfy constrains of 'query'
   */
  get(filter: GetFilesArg, signal: RequestOptions['signal']): Promise<GetFilesResult>;
  /**
   * Returns file/s created in files collection with 'file.meta' set according to 'query'
   */
  create(arg: CreateFilesArg): Promise<CreateFilesResult>;
  /**
   * Creates multiple files in a single request, each with its own metadata dto
   * (aligned by index with the uploaded files).
   */
  createMany(arg: CreateManyFilesArg): Promise<CreateFilesResult>;
  /**
   * Updates file in files collection where 'file.id' === 'dto.id'
   */
  update(dto: UpdateFileArg): Promise<UpdateFileResult>;
  /**
   * Deletes file from files collection where 'file.id' === 'id'
   */
  delete(id: DeleteFileArg): Promise<DeleteFileResult>;
  /**
   * Returns link to actual file stored on Google Cloud Storage
   */
  getLink(id: GetFileLinkArg, signal: RequestOptions['signal']): Promise<GetFileLinkResult>;
  /**
   * Returns content of actual file from Google Cloud Storage as blob
   * @deprecated Use `getContentV2` instead. TODO: remove as part of https://enmontech.atlassian.net/browse/ENMON-2931
   */
  getContent(bucketPath: GetFileContentArg, signal: RequestOptions['signal']): Promise<GetFileContentResult>;
  /**
   * Returns content of actual file from Google Cloud Storage as blob
   */
  getContentV2(fileId: GetFileContentArg, signal: RequestOptions['signal']): Promise<GetFileContentResult>;
  /**
   * Returns thumbnail link of actual file from Google Cloud Storage.
   */
  getThumbnail(id: SerializedFile['_id'], signal: RequestOptions['signal']): Promise<GetFileThumbnailResult>;
}

export class FilesApiClient extends BaseApiClient implements IFilesApiClient {
  constructor() {
    super('api/files');
  }

  public getOne(id: GetFileArg, signal: RequestOptions['signal']): Promise<GetFileResult> {
    return this.http.get(`${id}`, { signal });
  }

  public get(filter: GetFilesArg, signal: RequestOptions['signal']): Promise<GetFilesResult> {
    return this.http.get('', {
      query: getQuery(filter),
      signal,
    });
  }

  public create({ query, dto, files }: CreateFilesArg): Promise<CreateFilesResult> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append(CreateFileFormField.File, file);
    });

    if (dto) {
      formData.set(CreateFileFormField.Data, JSON.stringify(dto));
    }

    return uploadFile(this.http.urlPrefix, formData, {
      query: { ...query },
      signal: undefined,
    });
  }

  public createMany({ query, dtos, files }: CreateManyFilesArg): Promise<CreateFilesResult> {
    const formData = new FormData();
    for (const file of files) {
      formData.append(CreateFileFormField.File, file);
    }

    if (dtos.length) {
      formData.set(CreateFileFormField.Data, JSON.stringify(dtos));
    }

    return uploadFile(`${this.http.urlPrefix}/batch`, formData, {
      query: { ...query },
      signal: undefined,
    });
  }

  public update({ id, payload }: UpdateFileArg): Promise<UpdateFileResult> {
    return this.http.patch(`${id}`, payload);
  }

  public delete(id: DeleteFileArg): Promise<DeleteFileResult> {
    return this.http.delete(`${id}`);
  }

  public getLink(id: GetFileLinkArg, signal: RequestOptions['signal']): Promise<GetFileLinkResult> {
    return this.http.get(`${id}/link`, { signal });
  }

  /**
   * @deprecated Use `getContentV2` instead. TODO: remove as part of https://enmontech.atlassian.net/browse/ENMON-2931
   */
  public getContent(bucketPath: GetFileContentArg, signal: RequestOptions['signal']): Promise<GetFileContentResult> {
    return request(
      `${this.http.urlPrefix}/content/${bucketPath}`,
      {
        method: 'GET',
        signal,
      },
      response => response.blob(),
    );
  }

  public getContentV2(fileId: GetFileContentArg, signal: RequestOptions['signal']): Promise<GetFileContentResult> {
    return request(
      `${this.http.urlPrefix}/${fileId}/content`,
      {
        method: 'GET',
        signal,
      },
      response => response.blob(),
    );
  }

  public getThumbnail(id: SerializedFile['_id'], signal: RequestOptions['signal']): Promise<GetFileThumbnailResult> {
    return this.http.get(`${id}/thumbnail-link`, { signal });
  }
}
